import { msalInstance } from '../../root';

// Bug #8516: right after a redirect sign-in, root.tsx's account-settling logic can leave
// getActiveAccount() null for a moment before the async LOGIN_SUCCESS handler catches up, in
// two different shapes:
//  1. Zero accounts cached yet (redirect still in flight) — activeAccount and accounts are both
//     empty until MSAL writes the account.
//  2. Multiple accounts cached, active one deliberately cleared: root.tsx's own cleanup nulls
//     setActiveAccount() when getAllAccounts().length > 1, specifically so a stale/ambiguous
//     cache doesn't get used until LOGIN_SUCCESS picks the right one — getAllAccounts() is non-
//     empty here, only activeAccount is null.
// An earlier version of this fix only retried case 1 (looped on `accounts.length === 0`), so
// case 2 fell straight through to `accounts[0]` below — silently acquiring a token for an
// arbitrary cached account, the exact outcome root.tsx's cleanup exists to prevent (auto-review
// finding, fp:account-settle-loop-misses-stale-active-account). The loop now retries on
// `!activeAccount` alone, covering both; only once retries are exhausted does the pre-existing
// `accounts[0]` fallback apply, same as before this fix.
const ACCOUNT_SETTLE_RETRY_ATTEMPTS = 3;
const ACCOUNT_SETTLE_RETRY_DELAY_MS = 500;

/** Exported so callers that need to retry specifically this failure (e.g. usePlaygroundModeMutation)
 * compare against the same value instead of duplicating the literal — see
 * fp:retry-matches-literal-error-message (PR #2611). */
export const USER_NOT_SIGNED_IN_ERROR_MESSAGE = 'User is not signed in';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const acquireAccessToken = async (apiScopes: string[]) => {
  let activeAccount = msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
  let accounts = msalInstance.getAllAccounts();

  // SonarCloud typescript:S9382 ("unexpected await inside a loop") flags this line — that rule
  // targets independent work that could run concurrently via Promise.all. This loop is a
  // sequential poll-with-backoff instead: each read depends on the previous delay having
  // elapsed, so there is nothing here to parallelize.
  for (
    let attempt = 0;
    !activeAccount && attempt < ACCOUNT_SETTLE_RETRY_ATTEMPTS;
    attempt++
  ) {
    await delay(ACCOUNT_SETTLE_RETRY_DELAY_MS);
    activeAccount = msalInstance.getActiveAccount();
    accounts = msalInstance.getAllAccounts();
  }

  if (!activeAccount && accounts.length === 0) {
    /*
     * User is not signed in. Throw error or wait for user to login.
     * Do not attempt to log a user in outside of the context of MsalProvider
     */
    throw new Error(USER_NOT_SIGNED_IN_ERROR_MESSAGE);
  }
  const request = {
    scopes: apiScopes,
    account: activeAccount || accounts[0]
  };

  const authResult = await msalInstance.acquireTokenSilent(request);
  return authResult.accessToken;
};

export default acquireAccessToken;
